import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { AuthShell, IconInput } from "./AuthShell";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Loader2, ShieldCheck, Lock, KeyRound } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 1800);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    setLoading(false);
  };

  return (
    <AuthShell>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
        {done ? (
          <div className="text-center" data-testid="reset-done">
            <ShieldCheck className="h-12 w-12 text-emerald-500 mx-auto" />
            <h2 className="font-heading text-2xl font-bold text-slate-900 mt-4">Password reset</h2>
            <p className="text-sm text-slate-500 mt-2">Redirecting you to sign in…</p>
          </div>
        ) : (
          <>
            <h2 className="font-heading text-2xl font-bold text-slate-900">Set a new password</h2>
            <p className="text-sm text-slate-500 mt-1">Choose a strong password for your account.</p>
            {!token && (
              <div className="mt-4 text-sm bg-amber-50 border border-amber-200 text-amber-700 rounded-lg px-3 py-2">
                Missing reset token. Please use the link from your email.
              </div>
            )}
            {error && (
              <div className="mt-4 text-sm bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2">{error}</div>
            )}
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="password">New Password</Label>
                <IconInput icon={Lock} id="password" data-testid="reset-password" type="password" required minLength={8}
                  value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm">Confirm Password</Label>
                <IconInput icon={KeyRound} id="confirm" data-testid="reset-confirm" type="password" required
                  value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter password" />
              </div>
              <Button type="submit" data-testid="reset-submit" disabled={loading || !token}
                className="w-full bg-[#1E3A8A] hover:bg-[#1D4ED8] h-11">
                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Reset Password
              </Button>
            </form>
            <p className="mt-6 text-sm text-center text-slate-500">
              <Link to="/login" className="text-blue-700 font-semibold hover:underline">Back to Sign In</Link>
            </p>
          </>
        )}
      </div>
    </AuthShell>
  );
}
